<template>
  <div class="label-component" :class="rootClasses" :style="cssVars">
    <!-- If label has tooltip -->
    <cv-interactive-tooltip v-if="hasTooltip" :direction="tooltipDirection">
      <template #trigger>
        <span :title="tooltipText">{{ text }}</span>
      </template>

      <template #content>
        {{ tooltipText }}
      </template>
    </cv-interactive-tooltip>

    <!-- Else -->
    <ellipsis-text v-else :text="text" :title="titleText" :hide-title="hideTitle" />
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check";
import ColorUtils from "@/utils/color.utils.ts";
import subjectColorMap from "@configs/subjectColorMap";
import { computed } from "vue";

// Refer to Figma Common Component page for list of available kinds
// https://www.figma.com/file/nJ36YJweWZvR3bv4urW8LD/SLS-%E2%80%93-Common-Components?node-id=587%3A0

// Props
interface Props {
  kind?: string;
  text?: string;
  tooltipDirection?: string;
  tooltipText?: string;
  titleText?: string;
  hideTitle?: boolean;
  subjectKey?: string;
}

const {
  kind = "",
  text = "",
  tooltipDirection = "bottom",
  tooltipText = "",
  titleText = "",
  hideTitle = false,
  subjectKey = "",
} = defineProps<Props>();

// Computed
const rootClasses = computed(() => {
  return `${kind}`;
});

const hasTooltip = computed(() => {
  return !isEmpty(tooltipText);
});

const cssVars = computed(() => {
  const subjectColor = subjectColorMap.getSubjectColor(subjectKey);

  return {
    "--color": subjectColor,
    "--tinted-color": ColorUtils.getTintedColor(subjectColor),
    "--shaded-color": ColorUtils.getShadedColor(subjectColor),
    "--subject-text-color": isEmpty(subjectColor) ? "#6D7B8C" : "#FFFFFF",
  };
});
</script>

<style lang="scss">
@use "@styles/abstracts/functions" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/abstracts/mixins" as *;

.label-component {
  display: inline-flex;
  max-width: 100%;
  margin: $spacing-02;
  margin-left: 0;
  cursor: default;
  // .cv-interactive-tooltip {
  //   &:focus,
  //   &:active {
  //     pointer-events: none;
  //   }
  // }

  .bx--tooltip__trigger {
    margin-left: 0;
    cursor: pointer;

    span {
      cursor: pointer;
    }
  }

  span {
    @include carbon--type-style("helper-text-01", true);
    padding: $spacing-02 $spacing-03;
    cursor: default;
  }

  &.alp-stage {
    border-radius: 5px;

    span {
      display: inline-block;
      background-color: $tag-01;
      color: $text-04;
      border-radius: 5px;
      text-transform: uppercase;
    }

    &.ACTIVATE {
      span {
        background-color: $tag-06;
      }
    }

    &.PROMOTE {
      span {
        background-color: $tag-05;
      }
    }

    &.FACILITATE {
      span {
        background-color: $tag-04;
      }
    }

    &.MONITOR {
      span {
        background-color: $tag-03;
      }
    }
  }

  &.assigned {
    span {
      @include carbon--type-style("helper-text-01", true);
      color: $ui-03;
      background-color: $ui-05;
      border-radius: 5px;
    }
  }

  &.class-group,
  &.level-and-course,
  &.community {
    span {
      @include carbon--type-style("card-label-01", true);
      background-color: var(--tinted-color, $ui-01);
      color: var(--shaded-color, $text-02);
      border-radius: 5px;
    }
  }

  &.class-group {
    span {
      text-transform: uppercase;
    }
  }

  // browse community
  &.community-status {
    &.not-joined {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $interactive-02;
        border-radius: 5px;
      }
    }
  }

  &.external-resource {
    span {
      @include carbon--type-style("card-label-01");
      padding: $spacing-01 $spacing-03;
      text-transform: uppercase;
      color: $text-04;
      background-color: $poll-10;
      border-radius: 5px;
    }
  }

  &.location {
    span {
      @include carbon--type-style("card-label-01");
      padding: $spacing-01 $spacing-03;
      text-transform: uppercase;
      color: $text-04;
      background-color: $location-04;
      border-radius: 5px;
    }
  }

  &.game-choice {
    span {
      @include carbon--type-style("button-01");
      padding: $spacing-02 $spacing-03;
      text-transform: uppercase;
      color: $text-04;
      background-color: $tag-02;
      border-radius: 5px;
    }
  }

  &.subject {
    span {
      @include carbon--type-style("card-label-01", true);
      color: var(--subject-text-color);
      background-color: var(--color, $ui-01);
      border-radius: 5px;
    }
  }

  &.creditor {
    &.blank {
      span {
        cursor: default;
      }
    }

    span {
      color: $interactive-01;
      background-color: tint($interactive-01, 80%);
      border-radius: 0.9375rem;
      font-weight: 400;
      cursor: pointer;
    }
  }

  &.creditor-moe {
    span {
      color: $text-02;
      background-color: $disabled-01;
      border-radius: 0.9375rem;
      font-weight: 400;
      cursor: default;
    }
  }

  //assignment
  &.assignment-status,
  &.attempt-status {
    &.not-started {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $status-offline;
        border-radius: 5px;
      }
    }

    &.incomplete,
    &.draft {
      span {
        text-transform: uppercase;
        color: $state-info-text;
        background-color: $state-info;
        border-radius: 5px;
      }
    }

    &.complete {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-neutral;
        border-radius: 5px;
      }
    }

    &.overdue {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-negative;
        border-radius: 5px;
      }
    }

    &.restricted {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $hover-selected-ui;
        border-radius: 5px;
      }
    }

    &.upcoming {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $inverse-02;
        border-radius: 5px;
      }
    }

    &.paused {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $ui-05;
        border-radius: 5px;
      }
    }
  }

  //activity
  &.quiz-status,
  &.activity-status,
  &.section-status {
    &.not-started {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $status-offline;
        border-radius: 5px;
      }
    }

    &.incomplete {
      span {
        text-transform: uppercase;
        color: $state-info-text;
        background-color: $state-info;
        border-radius: 5px;
      }
    }

    &.complete,
    &.release {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-neutral;
        border-radius: 5px;
      }
    }

    &.pending-mark,
    &.marked {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-partial;
        border-radius: 5px;
      }
    }

    &.overdue {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-negative;
        border-radius: 5px;
      }
    }

    &.restricted {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $hover-selected-ui;
        border-radius: 5px;
      }
    }
  }

  &.quiz {
    &.incomplete {
      span {
        text-transform: uppercase;
        color: $state-info-text;
        background-color: $state-info;
        border-radius: 5px;
      }
    }

    &.completed {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-neutral;
        border-radius: 5px;
      }
    }

    &.pending-mark {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-partial;
        border-radius: 5px;
      }
    }

    &.marked {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-neutral;
        border-radius: 5px;
      }
    }

    &.overdue {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-negative;
        border-radius: 5px;
      }
    }

    &.blank {
      span {
        color: $text-04;
        background-color: $ui-04;
        border-radius: 5px;
      }
    }

    &.pending-quiz-score {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: #95a7bc;
        border-radius: 5px;
      }
    }
  }

  &.domain {
    span {
      color: $text-04;
      text-transform: uppercase;
      background-color: $inverse-support-04;
      border-radius: 5px;
      @include carbon--type-style("label-03", true);
    }

    &.community {
      span {
        background-color: $location-02;
      }
    }

    &.moe {
      span {
        background-color: $location-01;
      }
    }

    &.als {
      span {
        @include carbon--type-style("label-01", true);
        background-color: $inverse-hover-secondary;
        color: $tag-text-01;
        border-radius: 24px;
      }
    }
  }

  &.resource-status {
    &.resubmission {
      span {
        background-color: $tag-01;
        color: $text-04;
        border-radius: 5px;
        text-transform: uppercase;
      }
    }

    &.approved {
      span {
        background-color: $state-positive;
        color: $text-04;
        border-radius: 5px;
      }
    }

    &.draft,
    &.pending-unpublish,
    &.pending-approval {
      span {
        background-color: $state-neutral;
        color: $text-04;
        border-radius: 5px;
      }
    }

    &.rejected {
      span {
        background-color: $state-info;
        color: $active-danger;
        border-radius: 5px;
      }
    }

    &.archived {
      span {
        background-color: $disabled-02;
        color: $text-01;
        border-radius: 5px;
      }
    }

    &.unpublished {
      span {
        background-color: $state-negative;
        color: $text-04;
        border-radius: 5px;
      }
    }
  }

  &.no-error {
    span {
      color: $state-negative;
      border: 1px solid $state-negative;
      border-radius: 5px;
    }
  }

  &.section-range {
    color: $state-neutral;
    background-color: $highlight;
    border-radius: 5px;
  }

  &.resource-prerequisite {
    span {
      cursor: pointer;
      text-transform: uppercase;
      color: $text-04;
      background-color: $state-neutral;
      border-radius: 5px;
    }
  }

  &.to-review {
    span {
      @include carbon--type-style("card-label-01");
      height: 18px;
      display: flex;
      align-items: center;
      color: $state-info-text;
      background-color: $support-03;
      border-radius: 5px;
      text-transform: uppercase;
    }
  }

  &.audit-account-status {
    &.complete,
    &.activated,
    &.removed {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $state-neutral;
        border-radius: 5px;
      }
    }

    &.deactivated {
      span {
        text-transform: uppercase;
        color: $text-04;
        background-color: $interactive-02;
        border-radius: 5px;
      }
    }

    &.incomplete {
      span {
        text-transform: uppercase;
        color: $state-info-text;
        background-color: $state-info;
        border-radius: 5px;
      }
    }
  }

  // Content Mastery
  &.content-mastery {
    span {
      @include carbon--type-style("productive-heading-01", true);
      color: $ui-05;
      background-color: rgba($ui-05, 0.1);
      border-radius: 5px;
    }
  }

  &.new {
    span {
      @include carbon--type-style("card-label-01");
      height: 18px;
      background-color: $interactive-01;
      color: $ui-01;
      border-radius: 5px;
    }
  }

  &.gamified {
    height: 18px;
    position: relative;
    align-items: center;
    border-radius: 5px;
    text-transform: uppercase;
    background-color: $ui-01;
    z-index: z-map("default");

    &::before {
      @include position-mixin($top: 0, $right: 0, $bottom: 0, $left: 0, $pseudo: true);
      padding: $spacing-01;
      background: linear-gradient(90deg, #e7af04, #9c6904);
      border-radius: inherit;
      -webkit-mask:
        linear-gradient($ui-01 0 0) content-box,
        linear-gradient($ui-01 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }

    span {
      @include carbon--type-style("card-label-01");
      background: linear-gradient(90deg, #e7af04, #9c6904);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  &.als-content-mastery {
    span {
      @include carbon--type-style("productive-heading-02");
      color: $ui-05;
      background-color: rgba($ui-05, 0.1);
      border-radius: 5px;
    }
  }

  &.attempt {
    span {
      @include carbon--type-style("helper-text-01");
      color: $ui-01;
      background-color: $ui-05;
      border-radius: 5px;
    }
  }

  &.als-session-attempt {
    span {
      color: $text-04;
      text-transform: uppercase;
      background-color: $poll-02;
      border-radius: 5px;
      @include carbon--type-style("label-03", true);
    }
  }

  &.content-map-status {
    &.not-published {
      span {
        display: inline-flex;
        align-items: center;
        color: $ui-01;
        background-color: $ui-04;
        border-radius: 10px;
        font-size: 0;
        height: 18px;

        &:before {
          @include carbon--type-style("helper-text-01");
          content: "-";
          color: $ui-01;
        }
      }
    }

    &.published {
      span {
        @include carbon--type-style("helper-text-01");
        background-color: $state-neutral;
        color: $text-04;
        border-radius: 5px;
      }
    }
  }

  &.app-enabled {
    span {
      @include carbon--type-style("helper-text-01");
      background-color: $state-neutral;
      color: $text-04;
      border-radius: 10px;
    }
  }

  &.node-prerequisite {
    margin-left: $spacing-03;

    span {
      background-color: $state-neutral;
      color: $text-04;
      border-radius: 5px;
      cursor: pointer;
    }

    &.add {
      margin: 0;

      span {
        @include carbon--type-style("button-01");
        background-color: $ui-01;
        color: $interactive-01;
        cursor: pointer;
      }
    }
  }

  &.notification-category {
    span {
      @include carbon--type-style("card-label-01", true);
      border-radius: 5px;
      text-transform: uppercase;
    }

    &.general {
      span {
        background: $inverse-02;
        color: $ui-01;
      }
    }

    &.caco,
    &.school-admin {
      span {
        background: $announcement-admin;
        color: $ui-01;
      }
    }
  }

  // Game story and achievement condition
  &.game-condition {
    span {
      text-transform: uppercase;
      color: $text-04;
      background-color: $state-neutral;
      border-radius: 5px;
    }
  }

  &.xp {
    span {
      border-radius: 5px;
      background-color: $support-03;
      color: $ui-01;
    }
  }

  &.percentage-complete {
    span {
      color: $state-partial;
      background-color: rgba($state-partial, 0.2);
      border-radius: 5px;
    }
  }

  /* Whitelist website */
  &.website-enabled {
    span {
      color: $text-04;
      background-color: $state-neutral;
      border-radius: 5px;
    }
  }

  // Notification
  &.recipient {
    span {
      color: $state-neutral-text;
      background-color: tint($interactive-01, 90%);
      border-radius: 20px;
      font-weight: 400;
    }
  }

  // Live Chat Room (Conversation)
  &.conversation {
    span {
      @include carbon--type-style("card-label-01");
      padding: $spacing-01 $spacing-03;
      border-radius: 5px;
    }

    &.status-online {
      span {
        background-color: $status-online;
        color: $text-04;
      }
    }

    &.info {
      span {
        background-color: tint($support-04, 90%);
        color: $interactive-01;
      }
    }
  }

  &.visible-to-students {
    span {
      padding: $spacing-01 $spacing-03;
      background-color: $visibility-01-bg;
      color: $visibility-01-text;
      border-radius: 5px;
    }
  }
}
</style>
